import { useState, useEffect, useCallback, useRef } from 'react';

const DEMO_API    = process.env.REACT_APP_DEMO_API_URL || 'https://wgr9r4mj63.execute-api.us-east-1.amazonaws.com/prod';
const DEPLOY_API  = process.env.REACT_APP_API_URL      || 'https://w3jogbldyd.execute-api.us-east-1.amazonaws.com/prod';
const IDLE_MINUTES = 3;

const STATE_CFG = {
  ready:     { color: '#27ae60', bg: '#eafaf1', border: '#27ae60', icon: '🟢', label: 'Running' },
  running:   { color: '#27ae60', bg: '#eafaf1', border: '#27ae60', icon: '🟢', label: 'Running' },
  starting:  { color: '#e67e22', bg: '#fef5e7', border: '#f39c12', icon: '🟡', label: 'Starting…' },
  pending:   { color: '#e67e22', bg: '#fef5e7', border: '#f39c12', icon: '🟡', label: 'Pending…' },
  stopping:  { color: '#e67e22', bg: '#fef5e7', border: '#f39c12', icon: '🟡', label: 'Stopping…' },
  stopped:   { color: '#888',    bg: '#f9f9f9', border: '#ccc',    icon: '⚫', label: 'Stopped' },
  error:     { color: '#c0392b', bg: '#fdecea', border: '#e74c3c', icon: '🔴', label: 'Error' },
};

const ADMIN_EMAILS = ['anbunathan.r@gmail.com'];

export default function EC2Dashboard({ session }) {
  const isAdmin = ADMIN_EMAILS.includes(session?.email?.toLowerCase());
  const [status,    setStatus]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [starting,  setStarting]  = useState(false);
  const [stopping,  setStopping]  = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error,     setError]     = useState('');
  const pollRef  = useRef(null);
  const countRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res  = await fetch(`${DEMO_API}/demo-status`);
      const data = await res.json();
      setStatus(data);
      setError('');

      // Stop polling once ready
      if (data.status === 'ready' || data.status === 'running') {
        setStarting(false);
        clearInterval(pollRef.current);
        clearInterval(countRef.current);
        setCountdown(0);
      }
      if (data.status === 'stopped') {
        setStopping(false);
        clearInterval(pollRef.current);
      }
    } catch (e) {
      setError('Cannot reach demo API: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    return () => { clearInterval(pollRef.current); clearInterval(countRef.current); };
  }, [fetchStatus]);

  const startDemo = async () => {
    setStarting(true);
    setError('');
    try {
      const res  = await fetch(`${DEMO_API}/start-demo`, { method: 'POST' });
      const data = await res.json();
      setStatus(data);

      if (data.status === 'running' || data.status === 'ready') {
        setStarting(false);
        return;
      }

      // Start countdown
      let secs = data.estimatedWaitSeconds || 90;
      setCountdown(secs);
      clearInterval(countRef.current);
      countRef.current = setInterval(() => {
        setCountdown(c => Math.max(0, c - 1));
      }, 1000);

      // Poll every 5s
      clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchStatus, 5000);
    } catch (e) {
      setError('Start failed: ' + e.message);
      setStarting(false);
    }
  };

  const stopDemo = async () => {
    setStopping(true);
    setError('');
    try {
      const res  = await fetch(`${DEMO_API}/stop-demo`, { method: 'POST' });
      const data = await res.json();
      setStatus(data);
      // Poll until stopped
      clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchStatus, 5000);
    } catch (e) {
      setError('Stop failed: ' + e.message);
      setStopping(false);
    }
  };

  const st  = status?.status || 'stopped';
  const cfg = STATE_CFG[st] || STATE_CFG.stopped;
  const isRunning  = st === 'ready' || st === 'running';
  const isTransitioning = st === 'starting' || st === 'pending' || st === 'stopping' || starting || stopping;

  return (
    <div style={s.page}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div>
          <h2 style={s.heading}>🖥️ EC2 Multi-User Fleet</h2>
          <p style={s.sub}>
            g4dn.xlarge Spot instance with NICE DCV — on-demand 3D Unity simulation in browser
          </p>
        </div>
        <button style={s.refreshBtn} onClick={fetchStatus} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {error && <div style={s.errorBox}>⚠️ {error}</div>}

      {/* ── Instance status card ── */}
      <div style={{ ...s.instanceCard, borderTop: `4px solid ${cfg.border}`, background: cfg.bg }}>
        <div style={s.instanceTop}>
          <div style={s.instanceLeft}>
            <span style={{ fontSize: 36 }}>{cfg.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18, color: cfg.color }}>{cfg.label}</div>
              <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                g4dn.xlarge · NVIDIA T4 GPU · 4 vCPU · 16 GB RAM · NICE DCV
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                Instance: i-0516774891f677eb0
              </div>
            </div>
          </div>

          {/* Controls */}
          <div style={s.controls}>
            {!isRunning && !isTransitioning && (
              <button style={s.startBtn} onClick={startDemo}>
                Start Demo
              </button>
            )}
            {isTransitioning && (
              <button style={s.startBtn} disabled>
                <span style={s.spinner} /> {starting ? 'Starting...' : 'Stopping...'}
              </button>
            )}
            {isRunning && (
              <>
                <a href={status?.url} target="_blank" rel="noreferrer" style={s.openBtn}>
                  Open 3D Demo
                </a>
                <button style={s.stopBtn} onClick={stopDemo} disabled={stopping}>
                  {stopping ? 'Stopping...' : 'Stop Instance'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Countdown */}
        {countdown > 0 && (
          <div style={s.countdownBar}>
            <div style={{ ...s.countdownFill, width: `${Math.max(5, (90 - countdown) / 90 * 100)}%` }} />
            <span style={s.countdownText}>
              GPU instance booting… {countdown}s remaining
            </span>
          </div>
        )}

        {/* DCV URL when running */}
        {isRunning && status?.url && (
          <div style={s.urlRow}>
            <span style={{ fontSize: 12, color: '#555' }}>DCV URL:</span>
            <a href={status.url} target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: '#0073bb', fontWeight: 600 }}>
              {status.url}
            </a>
            <span style={{ fontSize: 11, color: '#888' }}>— accept self-signed cert in browser</span>
          </div>
        )}
        {isRunning && (
          <div style={{ marginTop: 10, fontSize: 12, background: '#fef5e7', padding: '8px 12px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#e67e22', fontWeight: 600 }}>Auto-stop in:</span>
            <span style={{ color: '#c0392b', fontWeight: 700, fontSize: 14 }}>
              {status?.remainingMinutes != null ? status.remainingMinutes + ' min' : IDLE_MINUTES + ' min'}
            </span>
            <span style={{ color: '#888' }}>| Hard limit: {status?.idleMinutes || IDLE_MINUTES} min from start</span>
          </div>
        )}
      </div>

      {/* ── Split architecture ── */}
      <SplitArchitecture />

      {/* ── Inject Fault ── */}
      {isAdmin && <InjectFault />}

      {/* ── Deploy build ── */}
      {isAdmin && <DeployBuild />}

    </div>
  );
}

function InjectFault() {
  const [status,  setStatus]  = useState('idle'); // idle | running | done | error
  const [message, setMessage] = useState('');

  const handleInject = async () => {
    setStatus('running'); setMessage('');
    try {
      const res  = await fetch(`${DEPLOY_API}/inject-fault`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      setStatus('done');
      setMessage(`✅ ${data.message}`);
    } catch (e) {
      setStatus('error');
      setMessage(`❌ ${e.message}`);
    }
  };

  return (
    <div style={s.card}>
      <h3 style={s.cardTitle}>⚡ Inject Fault</h3>
      <div style={{ fontSize: 13, color: '#555', marginBottom: 14 }}>
        Restarts Unity on the t3.small telemetry instance to trigger a fresh fault injection cycle.
        Faults will appear in the validation report within 1-2 minutes.
      </div>
      <button
        onClick={handleInject}
        disabled={status === 'running'}
        style={{ ...s.startBtn, background: status === 'running' ? '#aaa' : '#e67e22', fontSize: 13, padding: '10px 24px' }}
      >
        {status === 'running' ? '⏳ Restarting Unity…' : '⚡ Inject Fault'}
      </button>
      {message && (
        <div style={{ marginTop: 10, fontSize: 13, color: status === 'error' ? '#c0392b' : '#27ae60', background: status === 'error' ? '#fdecea' : '#eafaf1', padding: '8px 12px', borderRadius: 6 }}>
          {message}
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
        Targets: <code>t3.small (23.21.158.49)</code> via SSM
      </div>
    </div>
  );
}

function DeployBuild() {
  const [file,     setFile]     = useState(null);
  const [status,   setStatus]   = useState('idle');
  const [message,  setMessage]  = useState('');
  const [progress, setProgress] = useState(0);

  const handleFile = e => { const f = e.target.files[0]; if (f) { setFile(f); setStatus('idle'); setMessage(''); } };

  const handleDeploy = async () => {
    if (!file) return;
    setStatus('uploading'); setMessage(''); setProgress(10);
    try {
      const res1 = await fetch(`${DEPLOY_API}/deploy-build`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name }),
      });
      const { uploadUrl, deployId, s3Key } = await res1.json();
      if (!res1.ok) throw new Error('Failed to get upload URL');
      setProgress(15);

      // Multipart upload for large files — avoids Firefox XHR reset on large cross-origin PUT
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per part (S3 minimum)
      const totalParts = Math.ceil(file.size / CHUNK_SIZE);

      if (totalParts === 1) {
        // Small file — single PUT
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', uploadUrl);
          xhr.setRequestHeader('Content-Type', 'application/octet-stream');
          xhr.upload.onprogress = e => { if (e.lengthComputable) setProgress(15 + Math.round((e.loaded / e.total) * 45)); };
          xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
          xhr.onerror = () => reject(new Error('Upload network error'));
          xhr.timeout = 600000;
          xhr.send(file);
        });
      } else {
        // Large file — initiate multipart upload via Lambda
        const initRes = await fetch(`${DEPLOY_API}/deploy-build/${deployId}/multipart-init`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ s3Key }),
        });
        const { uploadId } = await initRes.json();

        const eTags = [];
        for (let i = 0; i < totalParts; i++) {
          const start = i * CHUNK_SIZE;
          const chunk = file.slice(start, start + CHUNK_SIZE);

          // Get pre-signed URL for this part
          const partRes = await fetch(`${DEPLOY_API}/deploy-build/${deployId}/multipart-part`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ s3Key, uploadId, partNumber: i + 1 }),
          });
          const { partUrl } = await partRes.json();

          const eTag = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', partUrl);
            xhr.upload.onprogress = e => {
              if (e.lengthComputable)
                setProgress(15 + Math.round(((i * CHUNK_SIZE + e.loaded) / file.size) * 45));
            };
            xhr.onload = () => xhr.status < 300
              ? resolve(xhr.getResponseHeader('ETag'))
              : reject(new Error(`Part ${i+1} failed: ${xhr.status}`));
            xhr.onerror = () => reject(new Error(`Part ${i+1} network error`));
            xhr.timeout = 120000;
            xhr.send(chunk);
          });
          eTags.push({ partNumber: i + 1, eTag });
        }

        // Complete multipart upload
        await fetch(`${DEPLOY_API}/deploy-build/${deployId}/multipart-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ s3Key, uploadId, parts: eTags }),
        });
      }

      setProgress(65); setStatus('deploying');
      setMessage('Build uploaded. Deploying to EC2 instances…');

      const res3 = await fetch(`${DEPLOY_API}/deploy-build/${deployId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key }),
      });
      const data = await res3.json();
      if (!res3.ok) throw new Error(data.message || 'Deploy failed');
      setProgress(100); setStatus('done');
      setMessage(`✅ Deployed to ${data.instances?.join(', ') || 'both instances'}. Unity restarted.`);
    } catch (e) { setStatus('error'); setMessage(`❌ ${e.message}`); }
  };

  return (
    <div style={s.card}>
      <h3 style={s.cardTitle}>🚀 Deploy Unity Build</h3>
      <div style={{ fontSize: 13, color: '#555', marginBottom: 14 }}>
        Upload a Unity Linux build zip to deploy to both t3.small and g4dn.xlarge automatically.
        Build from <code>File → Build Settings → Linux → Build</code>, then zip the output folder.
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ padding: '10px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500, background: file ? '#eafaf1' : '#f0f4f8', border: `1px solid ${file ? '#27ae60' : '#ccc'}` }}>
          📦 {file ? file.name : 'Choose build .zip'}
          <input type="file" accept=".zip" onChange={handleFile} style={{ display: 'none' }} />
        </label>
        <button onClick={handleDeploy} disabled={!file || status === 'uploading' || status === 'deploying'}
          style={{ ...s.startBtn, background: (!file || status === 'uploading' || status === 'deploying') ? '#aaa' : '#0073bb', fontSize: 13, padding: '10px 20px' }}>
          {status === 'uploading' ? '⬆ Uploading…' : status === 'deploying' ? '⚙ Deploying…' : '🚀 Deploy to EC2'}
        </button>
      </div>
      {(status === 'uploading' || status === 'deploying') && (
        <div style={{ marginTop: 12 }}>
          <div style={{ background: '#e0e0e0', borderRadius: 4, height: 6 }}>
            <div style={{ width: `${progress}%`, background: '#0073bb', height: '100%', borderRadius: 4, transition: 'width 0.4s' }} />
          </div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{status === 'uploading' ? 'Uploading…' : 'Deploying to EC2…'}</div>
        </div>
      )}
      {message && (
        <div style={{ marginTop: 10, fontSize: 13, color: status === 'error' ? '#c0392b' : '#27ae60', background: status === 'error' ? '#fdecea' : '#eafaf1', padding: '8px 12px', borderRadius: 6 }}>
          {message}
        </div>
      )}
      <div style={{ marginTop: 14, fontSize: 12, color: '#888' }}>
        Deploys to: <code>t3.small (23.21.158.49)</code> and <code>g4dn.xlarge (3.82.76.39)</code> via SSM. g4dn auto-starts if stopped.
      </div>
    </div>
  );
}

function SplitArchitecture() {
  return (
    <div style={s.card}>
      <h3 style={s.cardTitle}>Split Architecture — Telemetry vs 3D Visualization</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ background: '#eafaf1', borderRadius: 8, padding: 16, border: '1px solid #27ae60' }}>
          <div style={{ fontWeight: 700, color: '#27ae60', marginBottom: 8 }}>
            🟢 t3.small — Always On (24/7)
          </div>
          <div style={{ fontSize: 12, color: '#444', lineHeight: 1.7 }}>
            <b>Job:</b> Telemetry pipeline only<br />
            Publishes MQTT every 1s to IoT Core<br />
            → SiteWise → TwinMaker → Grafana<br />
            No GPU, no display, no DCV
          </div>
        </div>
        <div style={{ background: '#eaf4fb', borderRadius: 8, padding: 16, border: '1px solid #0073bb' }}>
          <div style={{ fontWeight: 700, color: '#0073bb', marginBottom: 8 }}>
            🖥️ g4dn.xlarge Spot — On-Demand
          </div>
          <div style={{ fontSize: 12, color: '#444', lineHeight: 1.7 }}>
            <b>Job:</b> 3D visualization only<br />
            Starts when user clicks "Start Demo"<br />
            Unity + NICE DCV streaming at 60 FPS<br />
            Auto-stops after 30 min idle
          </div>
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: '#666', background: '#f9f9f9', padding: 10, borderRadius: 6 }}>
        <b>config.json on t3.small:</b> enableTelemetry: true, enableRendering: false &nbsp;|&nbsp;
        <b>config.json on g4dn:</b> enableTelemetry: false, enableRendering: true
      </div>
    </div>
  );
}

const s = {
  page:         { maxWidth: 1100, margin: '0 auto', fontFamily: 'sans-serif' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  heading:      { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub:          { color: '#666', fontSize: 13, margin: 0 },
  refreshBtn:   { padding: '7px 14px', background: '#f0f4f8', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  errorBox:     { background: '#fdecea', color: '#c0392b', padding: '10px 16px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  instanceCard: { borderRadius: 8, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: 16 },
  instanceTop:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 },
  instanceLeft: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  controls:     { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  startBtn:     { padding: '12px 28px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 },
  openBtn:      { padding: '12px 24px', background: '#0073bb', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 14, textDecoration: 'none', display: 'inline-block' },
  stopBtn:      { padding: '12px 24px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  spinner:      { display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  countdownBar: { marginTop: 16, background: '#e0e0e0', borderRadius: 4, height: 8, position: 'relative', overflow: 'hidden' },
  countdownFill:{ height: '100%', background: '#f39c12', borderRadius: 4, transition: 'width 1s linear' },
  countdownText:{ position: 'absolute', top: 12, left: 0, fontSize: 12, color: '#666' },
  urlRow:       { marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: '#f0f4f8', padding: '8px 12px', borderRadius: 6 },
  card:         { background: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', marginBottom: 16 },
  fileLabel:    { padding: '10px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  cardTitle:    { fontSize: 15, fontWeight: 700, marginBottom: 12 },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  thead:        { background: '#f0f4f8' },
  th:           { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #ddd' },
  td:           { padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'middle' },
};
